// frontend/src/features/ups/UpsDriverStats.js
// This React component renders UPS driver stats & controls—agency selector, search box, 
// a toggle for global search, and counters that reflect the current filters (agency + query).
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectAllAgencies, fetchAgencies } from '../agencies/agenciesSlice';
import { selectFilters, setAgencyId, setQuery, setScope, selectStatsRefreshKey, } from '../ui/filtersSlice';

import StatusCounters from '../../components/StatusCounters';
import SiteSelector from '../../components/SiteSelector';

// ✅ Phase 8.7B: counts come from strict assignment reads + banned list
import { selectSelectedSiteId } from '../sites/sitesSlice';
import { fetchSiteAssignments } from '../../services/assignmentService';
import { fetchBannedDrivers } from '../../services/upsDriverService';

export default function UpsDriverStats() {
  const dispatch = useDispatch();
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q, scope } = useSelector(selectFilters);
  const siteId = useSelector(selectSelectedSiteId);
  const statsRefreshKey = useSelector(selectStatsRefreshKey);


  // StatusCounters expects keys like Active/Reserved/Banned (matches your UI routes)
  const [counts, setCounts] = useState({ Total: 0, Active: 0, Reserved: 0, Banned: 0 });

  // Ensure agencies are loaded for the dropdown
  useEffect(() => {
    if (!agencies?.length) dispatch(fetchAgencies());
  }, [dispatch, agencies?.length]);

  // ✅ Phase 8.7B: compute counts from correct sources of truth
  useEffect(() => {
    if (siteId == null) {
      setCounts({ Total: 0, Active: 0, Reserved: 0, Banned: 0 });
      return;
    }

    let ignore = false;
    (async () => {
      try {
        const agencyIdNum = agencyId ? Number(agencyId) : undefined;
        const search = q?.trim() ? q.trim() : undefined;

        // NOTE: scope toggle ("global" vs "tab")
        // - "tab": counts should match current site context
        // - "global": site-scoped for Active/Reserved because assignments are site lists.
        const [activeRows, reservedRows, bannedRows] = await Promise.all([
          fetchSiteAssignments(siteId, { statuses: ['active'], agencyId: agencyIdNum, search }),
          fetchSiteAssignments(siteId, { statuses: ['confirmed'], agencyId: agencyIdNum, search }),
          fetchBannedDrivers({ agencyId: agencyIdNum, search }),
        ]);

        if (!ignore) {
           const activeList = Array.isArray(activeRows) ? activeRows : [];
           const reservedList = Array.isArray(reservedRows) ? reservedRows : [];
           const bannedList = Array.isArray(bannedRows) ? bannedRows : [];
 
           // ✅ Do not count banned drivers inside operational buckets
           const Active = activeList.filter((r) => !r?.is_banned).length;
           const Reserved = reservedList.filter((r) => !r?.is_banned).length;
           const Banned = bannedList.length;
 
           setCounts({
             Total: Active + Reserved + Banned,
             Active,
             Reserved,
             Banned,

          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[UpsDriverStats] count fetch failed', err);
        if (!ignore) setCounts({ Total: 0, Active: 0, Reserved: 0, Banned: 0 });
      }
    })();

    return () => {
      ignore = true;
    };
  }, [siteId, agencyId, q, scope, statsRefreshKey]);

  const onAgencyChange = (e) => dispatch(setAgencyId(e.target.value)); 
  const onSearchChange = (e) => dispatch(setQuery(e.target.value));
  const onScopeToggle = (e) => dispatch(setScope(e.target.checked ? 'global' : 'tab'));

  const agenciesSorted = useMemo(() => {
    const list = Array.isArray(agencies) ? agencies : [];
    return [...list].sort((a, b) =>
      (a?.name ?? a?.agency_name ?? '').localeCompare(b?.name ?? b?.agency_name ?? '')
    );
  }, [agencies]);

  const getAgencyIdStr = (a) => String(a?.id ?? a?.agency_id ?? a?.agencyId ?? '');

  return (
    <div className="mb-4 space-y-3">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <SiteSelector />

        {/* Agency dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Agency</label>
          <select
            value={agencyId === '' ? '' : String(agencyId)}
            onChange={onAgencyChange}
            className="border rounded h-9 px-2 bg-white"
          >
            <option value="">All Agencies</option>
            {agenciesSorted.map((a) => {
              const idStr = getAgencyIdStr(a);
              return (
                <option key={idStr || (a?.name ?? a?.agency_name) || 'unknown'} value={idStr}>
                  {a?.name ?? a?.agency_name ?? '(Unnamed)'}
                </option>
              );
            })}
          </select>
        </div>

        {/* Search */}
        <div className="flex-1 min-w-[240px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            value={q}
            onChange={onSearchChange}
            placeholder="Search drivers…"
            className="border rounded h-9 px-2 w-full"
          />
        </div>

        {/* Scope toggle */}
        <label className="inline-flex items-center gap-2 text-sm pb-1">
          <input
            type="checkbox"
            checked={scope === 'global'}
            onChange={onScopeToggle}
          />
          <span>Search all statuses</span>
        </label>
      </div>

      {/* Figures */}
      <StatusCounters title="Drivers" counts={counts} />
    </div>
  );
}